import React, { useRef, useEffect } from "react";

const STATUS_STYLES = {
  today: {
    label: "text-white opacity-60",
    num: "text-white",
    dot: null,
  },
  covered: {
    chip: "bg-gray-50 border-[#EFEFEF]",
    label: "text-[#0A0A0A] opacity-60",
    num: "text-[#0A0A0A]",
    dot: null,
  },
  "has-gap": {
    chip: "bg-[#FFEBEA] border-[#E53935]",
    label: "text-[#E53935] opacity-80",
    num: "text-[#E53935]",
    dot: "bg-[#E53935]",
  },
  "has-warn": {
    chip: "bg-[#FFF4E0] border-[#F5A623]",
    label: "text-[#F5A623] opacity-80",
    num: "text-[#F5A623]",
    dot: "bg-[#F5A623]",
  },
};

export default function WeekStrip({ days = [], selectedDate, onSelectDay }) {
  const scrollRef = useRef(null);
  const todayRef = useRef(null);

  useEffect(() => {
    if (todayRef.current && scrollRef.current) {
      todayRef.current.scrollIntoView({ behavior: "instant", inline: "start", block: "nearest" });
      // nudge left so there's a peek of previous days
      scrollRef.current.scrollLeft = Math.max(0, scrollRef.current.scrollLeft - 72);
    }
  }, []);

  return (
    <>
      <style>{`
        .weekstrip-scroll::-webkit-scrollbar { display: none; }
        .weekstrip-scroll { scrollbar-width: none; }
      `}</style>
      <div
        ref={scrollRef}
        className="weekstrip-scroll flex gap-2 overflow-x-auto snap-x snap-mandatory"
      >
        {days.map(({ date, dayLabel, dayNum, status }) => {
          const s = STATUS_STYLES[status] || STATUS_STYLES.covered;
          const isSelected = selectedDate === date;
          const hasDot = status === "has-gap" || status === "has-warn";
          const isToday = status === "today";

          // Border + background: today always black bg, orange border (subtle or prominent).
          // Non-today selected: full orange fill + black border.
          // Non-today default: status-driven chip.
          let chipClass;
          if (isToday && isSelected) {
            chipClass = "border-2 border-[#F55C00] bg-[#0A0A0A]";
          } else if (isToday) {
            chipClass = "border border-transparent bg-[#0A0A0A]";
          } else if (isSelected) {
            chipClass = "border-2 border-[#0A0A0A] bg-[#F55C00]";
          } else {
            chipClass = `border ${s.chip}`;
          }

          const labelColor = !isToday && isSelected ? "text-[#0A0A0A] opacity-70" : s.label;
          const numColor   = !isToday && isSelected ? "text-[#0A0A0A]"            : s.num;

          return (
            <button
              key={date}
              ref={isToday ? todayRef : null}
              onClick={() => onSelectDay?.(date)}
              className={[
                // Mobile: fixed 64px; sm+: flex-1 so cells fill the row cleanly
                "relative flex-shrink-0 w-[64px] sm:flex-1 sm:w-auto aspect-square rounded-[10px]",
                "flex flex-col items-center justify-center",
                "snap-start",
                "transition-transform duration-150 hover:-translate-y-0.5 cursor-pointer",
                chipClass,
              ]
                .filter(Boolean)
                .join(" ")}
            >
              <span className={`text-[10px] font-bold uppercase tracking-widest ${labelColor}`}>
                {dayLabel}
              </span>
              <span className={`text-[17px] font-extrabold tracking-tight leading-none mt-0.5 ${numColor}`}>
                {dayNum}
              </span>
              {hasDot && (
                <span
                  className={`absolute bottom-[6px] w-[5px] h-[5px] rounded-full ${s.dot}`}
                />
              )}
            </button>
          );
        })}
      </div>
    </>
  );
}
